import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import UpdatesPanel from './UpdatesPanel';
import { trackSearch, trackCollegeRequestSubmit } from '../analytics';
import { Search, ChevronLeft, ChevronRight, Plus, MapPin, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ─── Card Skeleton ─── */
const CardSkeleton = () => (
  <div style={{
    background: '#fff',
    borderRadius: 32,
    padding: 28,
    border: '1px solid #efefef',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  }}>
    <div className="skeleton" style={{ height: 13, width: '55%', borderRadius: 6 }} />
    <div className="skeleton" style={{ height: 22, width: '80%', borderRadius: 6 }} />
    <div className="skeleton" style={{ height: 13, width: '45%', borderRadius: 6 }} />
    <div className="skeleton" style={{ height: 13, width: '35%', borderRadius: 6 }} />
    <div className="skeleton" style={{ height: 44, width: '100%', borderRadius: 14, marginTop: 8 }} />
  </div>
);

/* ─── College Card ─── */
const CollegeCard = ({ college, index }) => (
  <div
    className="animate-fade-up"
    style={{
      background: '#fff',
      borderRadius: 32,
      padding: 28,
      border: '1px solid #efefef',
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      cursor: 'default',
      animationDelay: `${Math.min(index * 0.05, 0.4)}s`,
    }}
    onMouseEnter={e => {
      e.currentTarget.style.boxShadow = 'rgba(0,0,0,0.18) 8px 16px 40px';
      e.currentTarget.style.transform = 'translateY(-3px)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    {/* Rank badge */}
    {college.nirfRank && (
      <span className="pill-badge pill-badge--blue" style={{ alignSelf: 'flex-start', marginBottom: 12 }}>
        NIRF #{college.nirfRank}
      </span>
    )}

    {/* Name */}
    <h2 style={{
      fontFamily: 'var(--font-primary)',
      fontWeight: 700,
      fontSize: 17,
      color: '#000',
      lineHeight: 1.3,
      marginBottom: 8,
    }}>
      {college.name}
    </h2>

    {/* Location */}
    <p style={{
      fontFamily: 'var(--font-mono)',
      fontSize: 12,
      color: '#999',
      letterSpacing: '0.04em',
      marginBottom: 4,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    }}>
      <MapPin size={11} strokeWidth={2} style={{ flexShrink: 0 }} />
      {college.city}{college.state ? `, ${college.state}` : ''}
    </p>

    {/* Rank label */}
    {college.rank && (
      <p style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        color: '#bcc1c7',
        letterSpacing: '0.04em',
        marginBottom: 16,
      }}>
        Rank: {college.rank}
      </p>
    )}

    <div style={{ flex: 1 }} />

    {/* CTA */}
    <Link to={`/college/${college._id}/reviews`} style={{ textDecoration: 'none', marginTop: 16 }}>
      <button
        className="btn-primary"
        id={`college-card-btn-${college._id}`}
        style={{ width: '100%', justifyContent: 'center', fontSize: 15 }}
      >
        See Reviews <ArrowRight size={15} strokeWidth={2} style={{ marginLeft: 4 }} />
      </button>
    </Link>
  </div>
);

/* ─── Request Modal ─── */
const RequestModal = ({ onClose }) => {
  const [fields, setFields] = useState({ name: '', city: '', state: '', nirfRank: '', rank: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = e => setFields(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/colleges/request-college`, fields);
      trackCollegeRequestSubmit(fields.name); // ← GA4 event
      setSuccess(true);
      setTimeout(onClose, 1800);
    } catch {
      alert('Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldLabels = { name: 'College Name', city: 'City', state: 'State', nirfRank: 'NIRF Rank', rank: 'Overall Rank' };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Request Submitted!</h2>
            <p style={{ color: '#666', fontSize: 15 }}>We'll review and add the college soon.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Request a College</h2>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999', lineHeight: 1 }}
              >×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {Object.entries(fieldLabels).map(([key, label]) => (
                <div className="form-group" key={key}>
                  <label htmlFor={`req-${key}`}>{label}</label>
                  <input
                    className="form-input"
                    type="text"
                    id={`req-${key}`}
                    name={key}
                    value={fields[key]}
                    onChange={handleChange}
                    required
                    placeholder={`Enter ${label.toLowerCase()}`}
                  />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isSubmitting}
                  style={{ flex: 2, justifyContent: 'center', opacity: isSubmitting ? 0.7 : 1 }}
                >
                  {isSubmitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

/* ─── HOME ─── */
const Home = () => {
  const navigate = useNavigate();
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [filterState, setFilterState] = useState('');   // state filter
  const [sortBy, setSortBy] = useState('nirf');          // nirf | az | za
  const searchRef = useRef(null);

  const PER_PAGE = 18;

  useEffect(() => {
    axios.get(`${API}/colleges`)
      .then(r => setColleges(r.data))
      .catch(() => setError('Could not load colleges. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  // Unique states list for filter chips
  const allStates = useMemo(() => {
    const s = new Set(colleges.map(c => c.state).filter(Boolean));
    return [...s].sort();
  }, [colleges]);

  const filtered = useMemo(() => {
    let list = colleges.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      (filterState === '' || c.state === filterState)
    );
    if (sortBy === 'az') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'za') list = [...list].sort((a, b) => b.name.localeCompare(a.name));
    else list = [...list].sort((a, b) => {
      const ra = parseInt(a.nirfRank) || 9999;
      const rb = parseInt(b.nirfRank) || 9999;
      return ra - rb;
    });
    return list;
  }, [colleges, searchQuery, filterState, sortBy]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const handleSearch = v => {
    setSearchQuery(v);
    setCurrentPage(1);
    trackSearch(v);
  };

  /* ── page number pills ── */
  const pageNumbers = () => {
    const pages = [];
    const range = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - range && i <= currentPage + range)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '…') {
        pages.push('…');
      }
    }
    return pages;
  };

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>

      {/* ── NAVBAR ── */}
      <nav style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #efefef',
        padding: '0 40px',
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 20,
      }}>
        {/* Logo */}
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <span style={{
            fontFamily: 'var(--font-primary)',
            fontWeight: 800,
            fontSize: 18,
            letterSpacing: '-0.02em',
          }}>
            Campus<span style={{ color: '#0000ff' }}>Reveal</span>
          </span>
        </button>

        {/* Search */}
        <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <Search
            size={15}
            strokeWidth={2}
            style={{
              position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none',
            }}
          />
          <input
            ref={searchRef}
            id="college-search"
            type="text"
            value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search colleges…"
            style={{
              width: '100%',
              height: 40,
              padding: '0 16px 0 40px',
              border: '1px solid #000',
              borderRadius: 9999,
              fontFamily: 'var(--font-primary)',
              fontSize: 14,
              outline: 'none',
              background: '#fff',
              transition: 'box-shadow 0.15s',
            }}
            onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(0,0,255,0.12)'}
            onBlur={e => e.target.style.boxShadow = 'none'}
          />
        </div>

        {/* Request */}
        <button
          onClick={() => setModalOpen(true)}
          className="btn-ghost"
          id="request-college-btn"
          style={{ fontSize: 14, padding: '9px 20px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> Request College
        </button>

        {/* Updates Bell */}
        <UpdatesPanel />
      </nav>

      {/* ── MAIN ── */}
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 40px' }}>

        {/* Header row */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{
            fontFamily: 'var(--font-primary)',
            fontWeight: 800,
            fontSize: 36,
            letterSpacing: '-0.03em',
            color: '#000',
            marginBottom: 6,
          }}>
            All Colleges
          </h1>
          <p style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            color: '#999',
            letterSpacing: '0.02em',
          }}>
            {loading ? 'Loading…' : `${filtered.length} college${filtered.length !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* ── FILTER BAR ── */}
        {!loading && (
          <div style={{ marginBottom: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Sort row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#999', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sort:</span>
              {[['nirf', 'NIRF Rank'], ['az', 'A → Z'], ['za', 'Z → A']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => { setSortBy(val); setCurrentPage(1); }}
                  style={{
                    padding: '5px 14px', borderRadius: 9999, fontSize: 12,
                    fontFamily: 'var(--font-mono)', cursor: 'pointer',
                    border: 'none', letterSpacing: '0.02em',
                    background: sortBy === val ? '#000' : '#f4f4f4',
                    color: sortBy === val ? '#fff' : '#555',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}

              {/* Clear all filters */}
              {(filterState !== '' || sortBy !== 'nirf') && (
                <button
                  onClick={() => { setFilterState(''); setSortBy('nirf'); setCurrentPage(1); }}
                  style={{
                    padding: '5px 12px', borderRadius: 9999, fontSize: 11,
                    fontFamily: 'var(--font-mono)', cursor: 'pointer',
                    border: '1px solid #ffcccc', background: 'transparent',
                    color: '#cc4444', letterSpacing: '0.02em',
                    transition: 'all 0.15s', marginLeft: 4,
                  }}
                >
                  ✕ Clear filters
                </button>
              )}
            </div>

            {/* State filter chips */}
            {allStates.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#999', letterSpacing: '0.06em', textTransform: 'uppercase' }}>State:</span>
                <button
                  onClick={() => { setFilterState(''); setCurrentPage(1); }}
                  style={{
                    padding: '5px 14px', borderRadius: 9999, fontSize: 12,
                    fontFamily: 'var(--font-mono)', cursor: 'pointer', border: 'none',
                    background: filterState === '' ? '#0000ff' : '#f4f4f4',
                    color: filterState === '' ? '#fff' : '#555',
                    transition: 'all 0.15s',
                  }}
                >
                  All
                </button>
                {allStates.map(st => (
                  <button
                    key={st}
                    onClick={() => { setFilterState(st); setCurrentPage(1); }}
                    style={{
                      padding: '5px 14px', borderRadius: 9999, fontSize: 12,
                      fontFamily: 'var(--font-mono)', cursor: 'pointer', border: 'none',
                      background: filterState === st ? '#0000ff' : '#f4f4f4',
                      color: filterState === st ? '#fff' : '#555',
                      transition: 'all 0.15s',
                    }}
                  >
                    {st}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Error */}
        {error && (
          <div style={{
            background: '#fff0f0',
            border: '1px solid #ffcccc',
            borderRadius: 16,
            padding: '20px 24px',
            color: '#cc0000',
            fontFamily: 'var(--font-primary)',
            fontSize: 15,
            marginBottom: 32,
          }}>
            {error}
          </div>
        )}

        {/* Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 20,
          marginBottom: 48,
        }}>
          {loading
            ? Array.from({ length: 9 }).map((_, i) => <CardSkeleton key={i} />)
            : paginated.map((college, i) => (
              <CollegeCard key={college._id} college={college} index={i} />
            ))
          }
        </div>

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🏛️</div>
            <h2 style={{ fontFamily: 'var(--font-primary)', fontWeight: 700, fontSize: 22, marginBottom: 8 }}>
              No colleges found
            </h2>
            <p style={{ color: '#999', fontSize: 15 }}>
              Try a different search, or{' '}
              <button
                onClick={() => setModalOpen(true)}
                style={{ color: '#0000ff', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: 0 }}
              >
                request it to be added
              </button>.
            </p>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            paddingBottom: 40,
          }}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{
                height: 38, padding: '0 14px', borderRadius: 9999,
                border: '1px solid #000', background: 'transparent',
                fontFamily: 'var(--font-primary)', fontSize: 14, fontWeight: 500,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                opacity: currentPage === 1 ? 0.35 : 1,
                transition: 'background 0.15s, color 0.15s',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
              onMouseEnter={e => { if (currentPage !== 1) { e.currentTarget.style.background = '#000'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#000'; }}
            >
              <ChevronLeft size={16} strokeWidth={2} /> Prev
            </button>

            {pageNumbers().map((p, i) =>
              p === '…' ? (
                <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: '#999', fontSize: 14 }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  style={{
                    width: 38, height: 38,
                    borderRadius: 9999,
                    border: p === currentPage ? 'none' : '1px solid #efefef',
                    background: p === currentPage ? '#0000ff' : 'transparent',
                    color: p === currentPage ? '#fff' : '#000',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 14,
                    fontWeight: p === currentPage ? 700 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {p}
                </button>
              )
            )}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={{
                height: 38, padding: '0 14px', borderRadius: 9999,
                border: '1px solid #000', background: 'transparent',
                fontFamily: 'var(--font-primary)', fontSize: 14, fontWeight: 500,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                opacity: currentPage === totalPages ? 0.35 : 1,
                transition: 'background 0.15s, color 0.15s',
                display: 'flex', alignItems: 'center', gap: 4,
              }}
              onMouseEnter={e => { if (currentPage !== totalPages) { e.currentTarget.style.background = '#000'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#000'; }}
            >
              Next <ChevronRight size={16} strokeWidth={2} />
            </button>
          </div>
        )}
      </main>

      {/* ── MODAL ── */}
      {modalOpen && <RequestModal onClose={() => setModalOpen(false)} />}
    </div>
  );
};

export default Home;
